import { AlertTriangle, Check, Copy, FolderOpen, Loader2, X } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { SourceImageSelect, useSourceImageOptions } from '../grid/SourceImagePicker';
import {
  MetadataFields,
  toUploadMetadata,
  withoutDerivedFields,
  withoutUnknown,
} from '../metadata/MetadataFields';
import {
  ASSET_RULES,
  ASSET_TYPE_KEYS,
  ASSET_TYPES,
  MAX_CAROUSELS_PER_BATCH,
  type AssetRules,
  type AssetType,
} from '../../constants/assetTypes';
import {
  emptyMetadataValue,
  type MetadataFieldKey,
  type MetadataFormValue,
} from '../../constants/metadataFields';
import { AssetDropzone } from './AssetDropzone';
import { CarouselFrameDetails, CarouselSelectList } from './CarouselDrafts';
import { detectCarousels, isCarouselUploadable, type DetectedCarousel } from './carouselDetect';
import { carouselBlocker } from './carouselUpload';
import {
  allowedTypesFor,
  MAX_BATCH_FILES,
  MAX_LABEL_LENGTH,
  validateFiles,
  type AcceptedFile,
  type RejectedFile,
} from './constants';
import { detectOlaAds, isUploadable, type OlaDetection, type PickedFile } from './olaDetect';
import { useBatchUpload, useCarouselUpload, useOlaUpload } from './useUploadRun';

/**
 * The upload flow, start to finish: select files, fill in the batch's
 * metadata, watch the uploads run.
 *
 * Metadata is collected per batch, not per file, and every field is optional
 * here. A batch of 500 files rarely shares one honest value for anything, so
 * forcing shared values at upload would either gate the upload or seed wrong
 * data. Whatever *is* filled applies to the whole batch as a convenience; the
 * rest is completed per asset afterwards in the grid's details drawer, which
 * edits the exact same field set (`MetadataFields`). Publishing is where
 * completeness is enforced — the backend skips any asset with gaps.
 *
 * The four kinds share the flow. Source images, master images and filmic are
 * the file route: one picked file, one asset, and the kind decides which files
 * the picker takes (stills for the first two, films for filmic) and which
 * metadata the form shows. OLA banners is the folder route, where one *folder*
 * containing an `index.html` becomes one asset owning a dozen-odd objects. The
 * routes diverge only in what "an item" means, so the phases, the metadata
 * form and the footer are shared and the two orchestrators sit behind
 * `useUploadRun`.
 */

type Phase = 'select' | 'details' | 'uploading';

/**
 * Which rules the batch is created under. Everything but `ola` and
 * `master_carousel` is the file route; a carousel is a master image in the
 * Carousel format, so it is picked under "Master images".
 */
type Mode = AssetRules;

/** The kind a mode belongs to, for the radio. */
const kindOfMode = (mode: Mode): AssetType => (mode === 'master_carousel' ? 'master_image' : mode);

const EMPTY_DETECTION: OlaDetection = { ads: [], skipped: [] };

interface UploadBatchModalProps {
  onClose: () => void;
  /** Called once uploads finish, so the caller can invalidate the asset list. */
  onUploaded: () => void;
}

/** Queued files, keyed for React and for removal before the upload starts. */
interface QueuedFile extends AcceptedFile {
  key: string;
}

export function UploadBatchModal({ onClose, onUploaded }: UploadBatchModalProps) {
  const [phase, setPhase] = useState<Phase>('select');
  const [queue, setQueue] = useState<QueuedFile[]>([]);
  const [rejected, setRejected] = useState<RejectedFile[]>([]);
  const [label, setLabel] = useState('');
  const [metadata, setMetadata] = useState<MetadataFormValue>(() => emptyMetadataValue());
  // The source image an OLA batch derives from. Optional, like the metadata:
  // left blank, the banners are linked later from the drawer while staged.
  // OLA-only, because a banner is always cut from one — the hero is a still,
  // uploaded as a source image.
  const [parentSourceImageId, setParentSourceImageId] = useState('');
  const [mode, setMode] = useState<Mode>('source_image');
  const [detection, setDetection] = useState<OlaDetection>(EMPTY_DETECTION);
  const [carousels, setCarousels] = useState<DetectedCarousel[]>([]);
  // Reading frame sizes is async; a newer drop must win over a slower older one.
  const [readingCarousels, setReadingCarousels] = useState(false);
  const carouselDrop = useRef(0);
  const nextFrameKey = useRef(0);
  const upload = useBatchUpload();
  const olaUpload = useOlaUpload();
  const carouselUpload = useCarouselUpload();

  const isOla = mode === 'ola';
  const isCarousel = mode === 'master_carousel';
  const active = isOla ? olaUpload : isCarousel ? carouselUpload : upload;
  // The picker only takes the current kind's files: a video dropped under
  // "Master images" is refused here by name rather than quietly becoming a
  // film, which is what used to happen when the bytes decided the kind.
  const allowedTypes = useMemo(() => allowedTypesFor(ASSET_RULES[mode].mediaKind), [mode]);
  const uploadableAds = useMemo(() => detection.ads.filter(isUploadable), [detection.ads]);
  // Upload-time linking is OLA-only even though a master image can also have
  // sources (`ASSET_TYPES.master_image.canHaveSources`). The OLA route inherits
  // from the source — the backend's `api/ola.py` applies `resolve_inheritance`
  // — whereas the file route would only record the link, so master images are
  // linked after upload from the detail rail. The gate at `batchMetadata` and
  // the picker below follow the same split.
  const { options: sourceImageOptions } = useSourceImageOptions(isOla || isCarousel);
  const derivedFrom = useMemo(
    () => sourceImageOptions.find((asset) => asset.id === parentSourceImageId) ?? null,
    [sourceImageOptions, parentSourceImageId],
  );

  // A monotonic counter, not the queue length: length goes back down when a
  // file is removed, so a length-derived key can be handed out twice. Dropping
  // a.png + b.png, removing a.png and dropping b.png again reproduces it, and
  // because `removeFile` filters by key, one click on X then deletes both rows.
  const nextKey = useRef(0);

  const handleFiles = useCallback(
    (picked: PickedFile[]) => {
      if (isCarousel) {
        // Replaces rather than merges, as for OLA: grouping is by folder, and
        // two drops merged would group against a tree that never existed.
        const drop = ++carouselDrop.current;
        setReadingCarousels(true);
        setRejected([]);
        void detectCarousels(picked, () => `c${nextFrameKey.current++}`).then((detected) => {
          if (drop !== carouselDrop.current) return;
          setCarousels(detected);
          setReadingCarousels(false);
        });
        return;
      }
      if (isOla) {
        // Replaces rather than merges. Two folder picks are two separate
        // deliveries, and merging them would re-root every ad against a tree
        // that never existed — the detector groups by path prefix.
        setDetection(detectOlaAds(picked));
        setRejected([]);
        return;
      }

      setQueue((current) => {
        const { accepted, rejected: newlyRejected } = validateFiles(
          picked.map(({ file }) => file),
          current.length,
          allowedTypes,
        );
        // Replaces rather than appends: rejections describe the drop that just
        // happened, and keeping the previous drop's complaints on screen makes
        // it look like the new files were refused too.
        setRejected(newlyRejected);
        return [...current, ...accepted.map((file) => ({ ...file, key: `f${nextKey.current++}` }))];
      });
    },
    [isOla, isCarousel, allowedTypes],
  );

  // Switching mode clears the other mode's selection. Carrying it across would
  // leave the footer counting files the current mode will never upload, and a
  // folder's members are not valid single-file uploads anyway.
  const handleModeChange = useCallback((next: Mode) => {
    setMode(next);
    setQueue([]);
    setDetection(EMPTY_DETECTION);
    carouselDrop.current += 1;
    setCarousels([]);
    setReadingCarousels(false);
    setRejected([]);
    // The picker is OLA-only; the file form has none.
    setParentSourceImageId('');
    // The metadata is kept, except "Unknown": only a source image may pick it,
    // and the form stops offering it, so it would go up unseen and be refused.
    if (kindOfMode(next) !== 'source_image') setMetadata(withoutUnknown);
    // And the format size, when the kind changes: a film and a master take
    // different sizes, so one picked for the old kind would go up unseen and
    // be refused.
    if (kindOfMode(next) !== kindOfMode(mode)) {
      setMetadata((current) => ({ ...current, format_size: '' }));
    }
  }, [mode]);

  const removeFile = useCallback((key: string) => {
    setQueue((current) => current.filter((file) => file.key !== key));
  }, []);

  const setMetadataField = useCallback(
    <K extends MetadataFieldKey>(field: K, value: MetadataFormValue[K]) => {
      setMetadata((current) => ({ ...current, [field]: value }));
    },
    [],
  );

  const handleStart = useCallback(async () => {
    setPhase('uploading');
    setRejected([]);
    // Only the fields this kind owns travel; see `toUploadMetadata` for why.
    const formMetadata = toUploadMetadata(metadata, mode);
    // With a source image picked, the backend copies the derived fields from it
    // and would overwrite anything sent for them — so they are not sent, and
    // the request says exactly what will be stored.
    const batchMetadata = isOla
      ? {
          ...(parentSourceImageId ? withoutDerivedFields(formMetadata) : formMetadata),
          ...(parentSourceImageId && { source_image_ids: [parentSourceImageId] }),
        }
      : formMetadata;

    const result = isOla
      ? await olaUpload.start(detection.ads, label, batchMetadata)
      : isCarousel
        ? // Sent as typed: the backend keeps it and fills only the blanks
          // from the frames' sources.
          await carouselUpload.start(carousels, label, formMetadata)
        : mode === 'source_image' || mode === 'master_image' || mode === 'filmic'
          ? await upload.start({ accepted: queue, assetType: mode }, label, batchMetadata)
          : null;
    if (result === null) return;

    // Fired even on a partial batch: whatever did confirm is staged and should
    // appear behind the modal, so the user can see it before deciding what to
    // do about the failures.
    if (result.uploaded > 0) onUploaded();
  }, [
    mode,
    isOla,
    isCarousel,
    carousels,
    carouselUpload,
    label,
    metadata,
    olaUpload,
    onUploaded,
    parentSourceImageId,
    queue,
    upload,
    detection.ads,
  ]);

  /**
   * The two orchestrators' items, flattened to one shape for the progress list.
   *
   * Normalised here rather than branched in the JSX: the rows differ only in
   * what the primary line says (a filename, or a banner name and its file
   * count), and duplicating forty lines of markup to express that would
   * guarantee the two drift.
   */
  const progressRows = useMemo(
    () =>
      isCarousel
        ? carouselUpload.items.map((item) => ({
            label: item.name,
            detail: `${item.frameCount} frame${item.frameCount === 1 ? '' : 's'}`,
            status: item.status,
            progress: item.progress,
            error: item.error,
            duplicateOf: item.duplicateOf,
            warningCount: undefined as number | undefined,
          }))
        : isOla
          ? olaUpload.items.map((item) => ({
              label: item.name,
              detail: `${item.fileCount} file${item.fileCount === 1 ? '' : 's'}`,
              status: item.status,
              progress: item.progress,
              error: item.error,
              duplicateOf: item.duplicateOf,
              warningCount: item.warningCount,
            }))
          : upload.items.map((item) => ({
              label: item.filename,
              detail: null as string | null,
              status: item.status,
              progress: item.progress,
              error: item.error,
              duplicateOf: item.duplicateOf,
              warningCount: undefined as number | undefined,
            })),
    [isOla, isCarousel, carouselUpload.items, olaUpload.items, upload.items],
  );

  const summary = useMemo(() => {
    const done = progressRows.filter((row) => row.status === 'done').length;
    const failed = progressRows.filter((row) => row.status === 'failed').length;
    const duplicates = progressRows.filter((row) => row.duplicateOf?.length).length;
    return { done, failed, duplicates };
  }, [progressRows]);

  const started = phase === 'uploading';
  const isFinished = started && !active.isUploading;

  /**
   * Send the failures back to the details step, keeping the metadata as typed.
   *
   * A failed batch is usually failed for a reason that outlives the click —
   * an expired signing link, a dropped connection — so the useful next move is
   * to run the same thing again, not to rebuild a 500-file selection from an
   * empty dropzone. Only the failed rows come back: the ones that confirmed are
   * already staged assets, and re-sending them would upload identical bytes
   * again for the library to flag as duplicates of themselves.
   *
   * Both orchestrators build their item list by mapping over their input in
   * order, so a row's index is its index in `queue` / `detection.ads`. That is
   * also why the filter is safe here: neither list can be edited while the
   * upload is in flight.
   */
  const handleRetryFailed = useCallback(() => {
    const failed = new Set(
      progressRows.flatMap((row, index) => (row.status === 'failed' ? [index] : [])),
    );
    if (isCarousel) {
      setCarousels((current) => current.filter((_, index) => failed.has(index)));
    } else if (isOla) {
      setDetection((current) => ({
        ...current,
        ads: current.ads.filter((_, index) => failed.has(index)),
      }));
    } else {
      setQueue((current) => current.filter((_, index) => failed.has(index)));
    }
    setPhase('details');
  }, [isOla, isCarousel, progressRows]);

  // Closing mid-flight would leave uploads running against a component that no
  // longer exists; the hook aborts on unmount, so this just makes it deliberate.
  const handleClose = useCallback(() => {
    if (active.isUploading) active.cancel();
    onClose();
  }, [active, onClose]);

  // The backdrop is a full-screen click target, so while bytes are moving it
  // does nothing: one stray click 20px outside the dialog should not silently
  // abort a 500-file batch that has no resume. Cancel stays available, but only
  // as the explicit button in the footer.
  const handleBackdropClick = useCallback(() => {
    if (active.isUploading) return;
    handleClose();
  }, [active.isUploading, handleClose]);

  /** How many assets the current mode would create. Drives the footer and gating. */
  const selectedCount = isCarousel ? carousels.length : isOla ? detection.ads.length : queue.length;
  const readyCarousels = carousels.filter(isCarouselUploadable).length;
  // Checked here, before any frame's details are asked for: the backend
  // refuses a larger batch whole, so filling one in would be wasted work.
  const tooManyCarousels = isCarousel && carousels.length > MAX_CAROUSELS_PER_BATCH;
  const canContinue = isCarousel
    ? !readingCarousels &&
      carousels.length > 0 &&
      !tooManyCarousels &&
      readyCarousels === carousels.length
    : isOla
      ? detection.ads.length > 0 && uploadableAds.length === detection.ads.length
      : queue.length > 0;
  // The details step's gate: a carousel also needs a source image on every
  // frame, which is only asked for there.
  const carouselsBlocked = isCarousel && carousels.some((carousel) => carouselBlocker(carousel));
  const canUpload = !carouselsBlocked;

  return (
    <>
      <div className="fixed inset-0 z-[90] bg-black/50" />
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        onClick={(event) => {
          if (event.target === event.currentTarget) handleBackdropClick();
        }}
        // Swallowed across the whole overlay, not just the dashed box: a drop
        // that misses the dropzone by a few pixels is otherwise handled by the
        // browser as a navigation, which unmounts the SPA and takes the queued
        // files with it.
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => event.preventDefault()}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="upload-batch-title"
          className={`flex max-h-[85vh] w-full flex-col rounded-lg bg-white shadow-xl ${
            isCarousel && phase === 'details' ? 'max-w-3xl' : 'max-w-lg'
          }`}
        >
          <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
            <h2 id="upload-batch-title" className="text-[14px] font-bold text-slate-800">
              {isFinished
                ? 'Upload complete'
                : started
                  ? 'Uploading…'
                  : phase === 'details'
                    ? 'Asset details'
                    : 'Upload assets'}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="ml-auto rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {phase === 'select' && (
              <>
                {/* Radiogroup rather than checkboxes: the four kinds produce
                    different assets, and "OLA" is not a modifier on a file
                    upload — a banner folder has no single file to modify. */}
                <div
                  role="radiogroup"
                  aria-label="What are you uploading?"
                  className="flex gap-1 rounded-lg bg-slate-100 p-1"
                >
                  {ASSET_TYPE_KEYS.map((value) => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={kindOfMode(mode) === value}
                      // Re-picking the current kind keeps its format choice.
                      onClick={() => kindOfMode(mode) !== value && handleModeChange(value)}
                      className={`flex-1 rounded-md px-3 py-1.5 text-[12.5px] font-medium transition-colors ${
                        kindOfMode(mode) === value
                          ? 'bg-white text-slate-800 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {ASSET_TYPES[value].pluralLabel}
                    </button>
                  ))}
                </div>

                {/* A master is one still or a carousel of 2–6. Picked here, at
                    upload, and fixed: a single still can never become a
                    carousel afterwards, nor a carousel a single still. */}
                {kindOfMode(mode) === 'master_image' && (
                  <div
                    role="radiogroup"
                    aria-label="Master image format"
                    className="flex gap-1 rounded-lg border border-slate-200 p-1"
                  >
                    {(
                      [
                        ['master_image', 'Single image'],
                        ['master_carousel', 'Carousel'],
                      ] as const
                    ).map(([value, text]) => (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={mode === value}
                        onClick={() => mode !== value && handleModeChange(value)}
                        className={`flex-1 rounded-md px-3 py-1 text-[12px] font-medium transition-colors ${
                          mode === value
                            ? 'bg-slate-100 text-slate-800'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        {text}
                      </button>
                    ))}
                  </div>
                )}

                <AssetDropzone
                  onFiles={handleFiles}
                  folderMode={isOla}
                  mediaKind={ASSET_RULES[mode].mediaKind}
                  carouselMode={isCarousel}
                />
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                    Batch label <span className="normal-case text-slate-400">(optional)</span>
                  </span>
                  <input
                    type="text"
                    value={label}
                    maxLength={MAX_LABEL_LENGTH}
                    onChange={(event) => setLabel(event.target.value)}
                    placeholder="e.g. Mustang launch — August"
                    className="rounded-md border border-slate-200 px-2 py-1.5 text-[12.5px] text-slate-800 outline-none focus:border-[#0014cc]"
                  />
                </label>
              </>
            )}

            {rejected.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50/60 p-2.5">
                <p className="flex items-center gap-1.5 text-[11.5px] font-medium text-amber-800">
                  <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} />
                  {rejected.length} file{rejected.length === 1 ? '' : 's'} can’t be uploaded
                </p>
                {/* Named individually rather than counted: "2 files rejected"
                    leaves the user to work out which of the forty they dropped. */}
                <ul className="mt-1.5 space-y-0.5">
                  {rejected.map((file) => (
                    <li key={file.filename} className="text-[11px] text-amber-700">
                      <span className="font-medium">{file.filename}</span> — {file.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {phase === 'select' && queue.length > 0 && (
              <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
                {queue.map((file) => (
                  <li key={file.key} className="flex items-center gap-2 px-2.5 py-1.5">
                    <span className="truncate text-[12px] text-slate-700" title={file.file.name}>
                      {file.file.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(file.key)}
                      aria-label={`Remove ${file.file.name}`}
                      className="ml-auto rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    >
                      <X className="h-3.5 w-3.5" strokeWidth={2} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {phase === 'select' && isOla && detection.ads.length > 0 && (
              <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
                {detection.ads.map((ad) => {
                  const blocked = !isUploadable(ad);
                  return (
                    <li key={ad.root} className={`px-2.5 py-2 ${blocked ? 'bg-red-50/50' : ''}`}>
                      <div className="flex items-center gap-2">
                        <FolderOpen
                          className={`h-3.5 w-3.5 shrink-0 ${
                            blocked ? 'text-red-500' : 'text-slate-400'
                          }`}
                          strokeWidth={2}
                        />
                        <span
                          className={`truncate text-[12px] font-medium ${
                            blocked ? 'text-red-800' : 'text-slate-700'
                          }`}
                          title={ad.root || ad.name}
                        >
                          {ad.name}
                        </span>
                        <span className="ml-auto shrink-0 text-[11px] text-slate-400">
                          {ad.files.length} file{ad.files.length === 1 ? '' : 's'}
                          {ad.posterRelpath ? ` · ${ad.posterRelpath}` : ''}
                        </span>
                      </div>
                      {/* Blocked banners stay listed rather than disappearing.
                          Picking a folder of four and seeing three, with no
                          word about the fourth, is the worst version of this. */}
                      {ad.issues.map((issue) => (
                        <p key={issue} className="mt-1 pl-5 text-[11px] text-red-700">
                          {issue}
                        </p>
                      ))}
                    </li>
                  );
                })}
              </ul>
            )}

            {phase === 'select' && isCarousel && readingCarousels && (
              <p className="flex items-center gap-1.5 text-[11.5px] text-slate-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Reading frame sizes…
              </p>
            )}

            {phase === 'select' && isCarousel && !readingCarousels && carousels.length > 0 && (
              <>
                <p className="text-[11px] text-slate-400">
                  Each folder is one carousel; loose images together make one. Frames run in
                  filename order — use the arrows to change it.
                </p>
                {tooManyCarousels && (
                  <p className="rounded-md border border-red-200 bg-red-50/60 px-2.5 py-2 text-[11.5px] text-red-700">
                    This drop has {carousels.length} carousels; one upload takes at most{' '}
                    {MAX_CAROUSELS_PER_BATCH}. Remove {carousels.length - MAX_CAROUSELS_PER_BATCH}{' '}
                    to continue, or upload them in two goes.
                  </p>
                )}
                <CarouselSelectList carousels={carousels} onChange={setCarousels} />
              </>
            )}

            {phase === 'select' &&
              isOla &&
              detection.ads.length > 0 &&
              detection.skipped.length > 0 && (
                <p className="text-[11px] text-slate-400">
                  {detection.skipped.length} file{detection.skipped.length === 1 ? '' : 's'} outside
                  any OLA folder {detection.skipped.length === 1 ? 'was' : 'were'} ignored.
                </p>
              )}

            {phase === 'select' &&
              isOla &&
              detection.ads.length === 0 &&
              detection.skipped.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50/60 p-2.5">
                  <p className="flex items-center gap-1.5 text-[11.5px] font-medium text-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} />
                    No OLAs found
                  </p>
                  <p className="mt-1 text-[11px] text-amber-700">
                    Nothing in that folder contains an index.html. Pick the folder the OLAs sit in —
                    nesting is fine, each index.html becomes its own asset.
                  </p>
                </div>
              )}

            {phase === 'details' && (
              <>
                {isCarousel && (
                  <CarouselFrameDetails
                    carousels={carousels}
                    onChange={setCarousels}
                    sourceImageOptions={sourceImageOptions}
                  />
                )}
                <p className="text-[11.5px] text-slate-500">
                  {isCarousel ? (
                    <>
                      Applies to all {selectedCount} carousel{selectedCount === 1 ? '' : 's'} in
                      this batch. Copy and source images are per frame, above.
                    </>
                  ) : isOla ? (
                    <>
                      Optional — applies to all {selectedCount} OLA
                      {selectedCount === 1 ? '' : 's'} in this batch. Anything left blank can be
                      filled in per asset afterwards; an OLA needs complete metadata and a source
                      image before it can be published.
                    </>
                  ) : (
                    <>
                      Optional — applies to all {selectedCount} file{selectedCount === 1 ? '' : 's'}{' '}
                      in this batch. Anything left blank can be filled in per asset afterwards;
                      assets need complete metadata before they can be published.
                    </>
                  )}
                </p>
                {isOla && (
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                      Source image
                    </span>
                    <SourceImageSelect
                      // One, deliberately. The batch's metadata is inherited
                      // from whatever it is linked to, and several sources
                      // disagreeing would have to be resolved across every file
                      // here — a question this step cannot usefully ask. Assets
                      // can be linked to more once they exist, in the drawer.
                      single
                      value={parentSourceImageId === '' ? [] : [parentSourceImageId]}
                      onChange={(ids) => setParentSourceImageId(ids[0] ?? '')}
                      options={sourceImageOptions}
                      emptyLabel="Not linked yet"
                      aria-label="Source image"
                    />
                    <span className="text-[11px] text-slate-400">
                      {sourceImageOptions.length === 0
                        ? 'No published source images yet. Upload and publish the hero image as a Source image; banners can be linked afterwards while staged.'
                        : 'Optional here; needed before publishing. Can also be linked afterwards while the banners are staged.'}
                    </span>
                  </label>
                )}
                <MetadataFields
                  value={metadata}
                  onFieldChange={setMetadataField}
                  // Off in both modes: a batch rarely shares one honest value
                  // for nameplate or tier, and a banner set even less so. What
                  // publish requires is enforced at publish, per asset.
                  showRequiredMarks={false}
                  mode="upload"
                  assetTypes={[mode]}
                  derivedFrom={derivedFrom}
                  inheritsFromFrames={isCarousel}
                />
              </>
            )}

            {started && (
              <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
                {progressRows.map((row, index) => (
                  <li key={index} className="flex items-center gap-2 px-2.5 py-1.5">
                    <span className="w-4 shrink-0">
                      {row.status === 'uploading' && (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-[#0014cc]" />
                      )}
                      {row.status === 'done' && <Check className="h-3.5 w-3.5 text-green-600" />}
                      {row.status === 'failed' && <X className="h-3.5 w-3.5 text-red-600" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[12px] text-slate-700" title={row.label}>
                        {row.label}
                        {row.detail && (
                          <span className="ml-1.5 text-[11px] text-slate-400">{row.detail}</span>
                        )}
                      </p>
                      {row.status === 'uploading' && (
                        <div className="mt-1 h-1 overflow-hidden rounded-full bg-slate-200">
                          <div
                            className="h-full bg-[#0014cc] transition-all duration-200"
                            style={{ width: `${row.progress}%` }}
                          />
                        </div>
                      )}
                      {row.error && <p className="text-[11px] text-red-600">{row.error}</p>}
                      {/* Advisory, and styled as such: matching bytes never
                          block an upload, so this must not look like an error. */}
                      {row.duplicateOf?.length ? (
                        <p className="flex items-center gap-1 text-[11px] text-slate-400">
                          <Copy className="h-3 w-3" strokeWidth={2} />
                          Already in the library
                        </p>
                      ) : null}
                      {/* The banner uploaded and will render — with a hole in
                          it. Amber rather than red, and named as a reference
                          problem, because the fix is in the delivered folder. */}
                      {row.warningCount ? (
                        <p className="flex items-center gap-1 text-[11px] text-amber-600">
                          <AlertTriangle className="h-3 w-3" strokeWidth={2} />
                          {row.warningCount} unresolved reference
                          {row.warningCount === 1 ? '' : 's'} — see the asset’s details
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center gap-2 border-t border-slate-200 px-4 py-3">
            {isFinished ? (
              <p className="text-[12px] text-slate-500">
                {summary.done} uploaded
                {summary.failed > 0 && `, ${summary.failed} failed`}
                {summary.duplicates > 0 && `, ${summary.duplicates} already in the library`}
              </p>
            ) : isCarousel ? (
              <p className="text-[12px] text-slate-500">
                {phase === 'details' && carouselsBlocked
                  ? 'Every frame needs a source image'
                  : carousels.length > 0
                    ? `${readyCarousels} of ${carousels.length} carousel${
                        carousels.length === 1 ? '' : 's'
                      } ready`
                    : 'No carousels selected'}
              </p>
            ) : isOla ? (
              <p className="text-[12px] text-slate-500">
                {detection.ads.length > 0
                  ? `${uploadableAds.length} of ${detection.ads.length} OLA${
                      detection.ads.length === 1 ? '' : 's'
                    } ready`
                  : 'No OLAs selected'}
              </p>
            ) : (
              <p className="text-[12px] text-slate-500">
                {queue.length > 0
                  ? `${queue.length} of ${MAX_BATCH_FILES} file${queue.length === 1 ? '' : 's'}`
                  : 'No files selected'}
              </p>
            )}

            {active.isUploading ? (
              <button
                type="button"
                onClick={active.cancel}
                className="ml-auto rounded-lg px-3 py-1.5 text-[12.5px] font-medium text-red-600 hover:bg-red-50"
              >
                Cancel
              </button>
            ) : isFinished ? (
              <>
                {summary.failed > 0 && (
                  <button
                    type="button"
                    onClick={handleRetryFailed}
                    className="ml-auto rounded-lg px-3 py-1.5 text-[12.5px] font-medium text-slate-600 hover:bg-slate-100"
                  >
                    Try again
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className={`rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-[#0010a3] ${
                    summary.failed > 0 ? '' : 'ml-auto'
                  }`}
                >
                  Done
                </button>
              </>
            ) : phase === 'select' ? (
              <button
                type="button"
                disabled={!canContinue}
                onClick={() => setPhase('details')}
                className="ml-auto rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-[#0010a3] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
              >
                Continue
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setPhase('select')}
                  className="ml-auto rounded-lg px-3 py-1.5 text-[12.5px] font-medium text-slate-600 hover:bg-slate-100"
                >
                  Back
                </button>
                <button
                  type="button"
                  disabled={!canUpload}
                  onClick={() => void handleStart()}
                  className="rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-[#0010a3] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                >
                  Upload {selectedCount > 0 ? selectedCount : ''}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
